<script setup lang="ts">
/**
 * Body of /ats-friendly-resume-template.
 *
 * Order: the answer, the eight properties that make a layout parser-safe, the
 * proof (the text our Basic template really hands an ATS), our templates
 * against those properties, then the traps in downloaded templates. One offer,
 * at the end.
 */
const { t } = useI18n()

const rules = ['column', 'headings', 'text', 'contact', 'dates', 'fonts', 'bullets', 'file'] as const
const templates = ['basic', 'ats', 'classic', 'minimal', 'modern'] as const
const traps = ['textBoxes', 'tables', 'icons', 'headerFooter', 'bars', 'creative'] as const

/** pdftotext output, one line per array item. */
const sampleLines = computed(() => t('atsTemplateGuide.reads.sample').split('\n'))
</script>

<template>
  <div class="sofax">
    <!-- The answer, before anything else on the page -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space-sm">
        <div class="shell">
          <div class="mx-auto max-w-3xl rounded-[0.625rem] border-l-4 border-accent bg-surface p-[1.875rem] dark:bg-gray-800">
            <div class="mb-3 text-sm font-semibold uppercase tracking-wide text-muted dark:text-gray-400">
              {{ t('atsTemplateGuide.short.label') }}
            </div>
            <p class="text-lg">{{ t('atsTemplateGuide.short.text') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- The eight properties -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space-bottom">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('atsTemplateGuide.rules.eyebrow')"
            :title="t('atsTemplateGuide.rules.title')"
            :text="t('atsTemplateGuide.rules.lead')"
            width="max-w-4xl"
          />
          <ol class="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-2">
            <li
              v-for="(rule, index) in rules"
              :key="rule"
              class="rounded-[0.625rem] bg-surface p-[1.875rem] dark:bg-gray-800"
            >
              <div class="flex items-start gap-5">
                <span
                  class="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-ink !text-base !font-bold !text-white dark:bg-white dark:!text-ink"
                  aria-hidden="true"
                >{{ index + 1 }}</span>
                <div class="flex-1">
                  <h3 class="!text-xl">{{ t(`atsTemplateGuide.rules.items.${rule}.title`) }}</h3>
                  <p class="mt-3 text-base">{{ t(`atsTemplateGuide.rules.items.${rule}.text`) }}</p>
                </div>
              </div>
            </li>
          </ol>
        </div>
      </div>
    </section>

    <!-- The proof: what a parser actually gets -->
    <section class="surface-alt">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('atsTemplateGuide.reads.eyebrow')"
            :title="t('atsTemplateGuide.reads.title')"
            :text="t('atsTemplateGuide.reads.lead')"
            width="max-w-4xl"
          />
          <figure class="mx-auto max-w-3xl">
            <div class="overflow-x-auto rounded-[0.625rem] bg-ink p-6 font-mono text-sm leading-relaxed text-white dark:bg-gray-800">
              <div v-for="(line, i) in sampleLines" :key="i" class="min-h-[1.625em] whitespace-pre-wrap">{{ line }}</div>
            </div>
            <figcaption class="mt-4 text-sm text-muted dark:text-gray-400">{{ t('atsTemplateGuide.reads.caption') }}</figcaption>
          </figure>
          <p class="mx-auto mt-8 max-w-3xl text-lg">{{ t('atsTemplateGuide.reads.after') }}</p>
        </div>
      </div>
    </section>

    <!-- Our templates against the list -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('atsTemplateGuide.ours.eyebrow')"
            :title="t('atsTemplateGuide.ours.title')"
            :text="t('atsTemplateGuide.ours.lead')"
            width="max-w-4xl"
          />
          <div class="mx-auto max-w-5xl overflow-x-auto">
            <table class="w-full border-collapse text-left text-base">
              <thead>
                <tr>
                  <th
                    v-for="col in ['template', 'layout', 'best', 'free']"
                    :key="col"
                    scope="col"
                    class="border border-line bg-surface px-4 py-3 font-title font-semibold text-ink dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  >
                    {{ t(`atsTemplateGuide.ours.head.${col}`) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="id in templates" :key="id">
                  <th scope="row" class="whitespace-nowrap border border-line px-4 py-3 align-top font-semibold text-ink dark:border-gray-700 dark:text-white">
                    {{ t(`cv.templates.${id}.name`) }}
                  </th>
                  <td class="border border-line px-4 py-3 align-top dark:border-gray-700">{{ t(`atsTemplateGuide.ours.rows.${id}.layout`) }}</td>
                  <td class="border border-line px-4 py-3 align-top dark:border-gray-700">{{ t(`atsTemplateGuide.ours.rows.${id}.best`) }}</td>
                  <td class="border border-line px-4 py-3 align-top dark:border-gray-700">{{ t(`atsTemplateGuide.ours.rows.${id}.free`) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="mx-auto mt-6 max-w-5xl text-base text-muted dark:text-gray-400">
            {{ t('atsTemplateGuide.ours.note') }}
            <LocaleLink to="/templates" class="link">{{ t('atsTemplateGuide.ours.link') }} →</LocaleLink>
          </p>
        </div>
      </div>
    </section>

    <!-- Traps in downloaded templates -->
    <section class="surface-alt">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('atsTemplateGuide.traps.eyebrow')"
            :title="t('atsTemplateGuide.traps.title')"
            :text="t('atsTemplateGuide.traps.lead')"
            width="max-w-4xl"
          />
          <ul class="mx-auto flex max-w-3xl flex-col gap-4">
            <li
              v-for="trap in traps"
              :key="trap"
              class="rounded-[0.625rem] bg-white p-[1.875rem] dark:bg-gray-800"
            >
              <h3 class="!text-xl">{{ t(`atsTemplateGuide.traps.items.${trap}.title`) }}</h3>
              <p class="mt-3 text-base">{{ t(`atsTemplateGuide.traps.items.${trap}.text`) }}</p>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- The one offer on the page -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell">
          <div class="mx-auto max-w-3xl text-center">
            <div class="eyebrow mb-5">{{ t('atsTemplateGuide.next.eyebrow') }}</div>
            <h2 class="!text-3xl md:!text-[2.5rem]">{{ t('atsTemplateGuide.next.title') }}</h2>
            <p class="mt-5 text-lg">{{ t('atsTemplateGuide.next.p1') }}</p>
            <div class="mt-8">
              <SofaxBtn to="/free-cv-builder" :label="t('atsTemplateGuide.next.cta')" variant="accent" />
            </div>
            <p class="mt-6">
              <LocaleLink to="/blog/how-to-create-an-ats-friendly-resume-get-past-applicant-tracking-systems" class="link">
                {{ t('atsTemplateGuide.next.writingLink') }} →
              </LocaleLink>
            </p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
